import { useState, useMemo } from "react";
import dayjs from "dayjs";
import {
  useGetConsumerOutReports,
  useDownloadConsumerOutReportExcel
} from "@app/services/SupportsConsult/hooks";
import { useGetUsersByModule } from "@app/services/common/hooks";
import { downloadExcel } from "@app/utils/downloadExcel";

import ExcelIcon from "@app/assets/excel.svg";

import { ActionIcon, Grid, Image, Select, Table, TextInput } from "@mantine/core";
import { Template } from "@app/common/components/Template";
import { FilterSearchButton } from "@app/common/components/SearchButtonFilters";
import AppPagination from "@app/common/components/AppPagination";

import type {
  IConsumerOutReportFilters,
  ISupportConsultResponse
} from "@app/services/SupportsConsult/types";

const today = dayjs().format("YYYY-MM-DD");

const ConsumerOutReport = () => {
  const [filters, setFilters] = useState<IConsumerOutReportFilters>({
    deliveredBy: "",
    startDate: today,
    endDate: today,
    search: "",
    page: 1,
    perpage: 10,
    trigger: 0
  });

  const { data: supportList } = useGetConsumerOutReports(filters);
  const { data: usersData } = useGetUsersByModule();
  const { refetch: downloadExcel_ } = useDownloadConsumerOutReportExcel(filters);

  const usersList = useMemo(() => {
    if (!usersData?.results) {
      return [];
    }
    return usersData.results.map((user) => ({
      value: String(user.id),
      label: `${user.first_name} ${user.first_surname}${user.second_surname ? ` ${user.second_surname}` : ""}`
    }));
  }, [usersData?.results]);

  const handleSubmitFilters = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setFilters((state) => ({ ...state, page: 1, trigger: (state.trigger ?? 0) + 1 }));
  };

  const handleDownloadExcel = async () => {
    const result = await downloadExcel_();
    if (result.data?.results?.base64) {
      downloadExcel(result.data.results.base64, "informe-salida-consumo.xlsx");
    }
  };

  const FiltersHeader = () => {
    return (
      <Grid align="flex-end" mb="lg" w="100%" data-testid="consumeroutreport-filters">
        <Grid.Col span={2}>
          <TextInput
            data-testid="consumeroutreport-start-date-input"
            label="Fecha desde"
            type="date"
            max={filters.endDate || undefined}
            placeholder="Escribir..."
            value={filters.startDate}
            onChange={({ target }) => {
              setFilters((state) => ({
                ...state,
                startDate: target.value
              }));
            }}
          />
        </Grid.Col>
        <Grid.Col span={2}>
          <TextInput
            min={filters.startDate || undefined}
            data-testid="consumeroutreport-end-date-input"
            label="Fecha hasta"
            type="date"
            placeholder="Escribir..."
            value={filters.endDate}
            onChange={({ target }) => {
              setFilters((state) => ({
                ...state,
                endDate: target.value
              }));
            }}
          />
        </Grid.Col>
        <Grid.Col span={2}>
          <Select
            data-testid="consumeroutreport-user-select"
            label="Usuario"
            data={usersList}
            clearable
            onChange={(value) => {
              setFilters((state) => ({
                ...state,
                deliveredBy: value
              }));
            }}
            searchable
            placeholder="Seleccionar..."
          />
        </Grid.Col>
        <Grid.Col span={2}>
          <FilterSearchButton />
        </Grid.Col>
        <Grid.Col span="auto" className="flex justify-end">
          <ActionIcon
            variant="subtle"
            onClick={handleDownloadExcel}
            data-testid="consumeroutreport-download-button"
          >
            <Image src={ExcelIcon} />
          </ActionIcon>
        </Grid.Col>
      </Grid>
    );
  };

  const renderRow = (option: ISupportConsultResponse, index: number) => {
    return (
      <Table.Tr key={option?.disId} data-testid={`consumeroutreport-row-${index}`}>
        <Table.Td ta={"center"}>{option?.medicineCode}</Table.Td>
        <Table.Td>{option?.medicineName}</Table.Td>
        <Table.Td ta={"center"}>{option?.lot}</Table.Td>
        <Table.Td>{option?.deliveredByName}</Table.Td>
        <Table.Td ta={"center"}>{option?.qtyDelivered}</Table.Td>
      </Table.Tr>
    );
  };

  const renderTable = () => {
    return (
      <Table className="biowel-table" data-testid="consumeroutreport-table">
        <Table.Thead>
          <Table.Tr>
            <Table.Th ta={"center"}>Código artículo</Table.Th>
            <Table.Th>Medicamento</Table.Th>
            <Table.Th ta={"center"}>Lote</Table.Th>
            <Table.Th>Usuario que dispensó</Table.Th>
            <Table.Th ta={"center"}>Cantidad</Table.Th>
          </Table.Tr>
        </Table.Thead>

        <Table.Tbody>
          {supportList?.results?.length ? (
            supportList?.results?.map((item, index) => renderRow(item, index))
          ) : (
            <Table.Tr>
              <Table.Td ta="center" colSpan={12} data-testid="consumeroutreport-no-data">
                No hay información para mostrar.
              </Table.Td>
            </Table.Tr>
          )}
        </Table.Tbody>
      </Table>
    );
  };

  return (
    <Template title="Informe salida para consumo">
      <form onSubmit={handleSubmitFilters} data-testid="consumeroutreport-form">
        {FiltersHeader()}
      </form>

      {renderTable()}

      <AppPagination
        total={supportList?.rowTotal || 0}
        withDetails
        value={filters.page || 1}
        onChange={(value) => setFilters((state) => ({ ...state, page: value }))}
        data-testid="consumeroutreport-pagination"
      />
    </Template>
  );
};

export default ConsumerOutReport;
